import type { AnchorHTMLAttributes, ComponentProps, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

export const ALLOWED_ELEMENTS = [
	'a',
	'p',
	'i',
	'b',
	'u',
	'span',
	'br',
	'strong',
	'em',
	'ul',
	'ol',
	'li',
	'blockquote',
	'code',
	'pre',
	'hr',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'del',
	's',
	'sub',
	'sup',
	'small',
	'mark'
];

const REHYPE_PLUGINS = [rehypeRaw];

type MarkdownProps = Omit<ComponentProps<typeof ReactMarkdown>, 'components'> & {
	href?: string;
	linkClassNames?: string;
};

type AnchorComponentProps = ComponentProps<'a'> & {
	node?: unknown;
};

type MarkdownLinkProps = AnchorComponentProps & {
	linkHref?: string;
	linkClassNames?: string;
};

function MarkdownLink({ linkClassNames, linkHref, href, ...props }: MarkdownLinkProps) {
	return <Link {...props} className={linkClassNames ?? props.className} href={linkHref || href} />;
}

function getMarkdownComponents({ href, linkClassNames }: Pick<MarkdownProps, 'href' | 'linkClassNames'>) {
	return {
		a: ({ node, ...props }: AnchorComponentProps) => {
			return <MarkdownLink {...props} linkClassNames={linkClassNames} linkHref={href} />;
		}
	};
}

export function Markdown({ href, linkClassNames, ...rest }: MarkdownProps) {
	return (
		<ReactMarkdown
			allowedElements={ALLOWED_ELEMENTS}
			rehypePlugins={REHYPE_PLUGINS}
			unwrapDisallowed
			{...rest}
			components={getMarkdownComponents({ href, linkClassNames })}
		/>
	);
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
	children?: ReactNode;
};

function Link(props: LinkProps) {
	return (
		<a {...props} rel="noopener noreferrer" target="_blank">
			{props.children}
		</a>
	);
}
